import { createContext, useEffect, useReducer } from 'react';

import { useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

import { makeHTTPProvider } from 'shared/infra/providers';
import { GOOGLE_CLICK_ID_PARAMS } from 'shared/presentation/constants';
import { getGoogleClickIdentifier } from 'shared/presentation/helpers';

import { INTERNAL_PROMO, UTM } from './AppContext.constants';
import resolveUtmCookieUpdates from './helpers/resolveUtmCookieUpdates';
import useLogger from '../services/Logger';

export { INTERNAL_PROMO, UTM };

const ACTIONS = {
  PROCESS_URL: 'process_url',
  LOGOUT: 'logout',
  UPDATE_STATE: 'update_state',
};

// Capturados na carga do módulo (antes de qualquer render/efeito React) para
// preservar a URL de entrada original. useAddUtmCookiesToCurrentPage (em
// Routes.tsx, descendente de AuthProvider) reescreve window.location usando
// os cookies de UTM já salvos assim que monta — como efeitos de filhos
// disparam antes dos do pai, isso aconteceria antes do PROCESS_URL abaixo se
// ele lesse window.location ao vivo, apagando a query string real do banner
// interno antes que pudéssemos processá-la (ED-6914).
const INITIAL_URL = new URL(window.location.href);
const INITIAL_SEARCH_PARAMS = INITIAL_URL.searchParams;
const INITIAL_PATHNAME = INITIAL_URL.pathname;

const TENANCIAS = {
  BRASIL: 'vialaser',
  PARAGUAI: 'vialaserparaguay',
};

const AppContext = createContext({
  globalState: {},
});

const initialState = {
  hideMenus: false,
  logo: null,
  tenancia: TENANCIAS.BRASIL,
  banners: null,
};

function AuthProvider(props) {
  const theme = useTheme();
  const isUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const { i18n } = useTranslation();
  const logger = useLogger();

  initialState.tenancia =
    i18n.language === 'pt' ? TENANCIAS.BRASIL : TENANCIAS.PARAGUAI;

  const [state, dispatch] = useReducer((oldState, action) => {
    const newState = {};

    function getNewState(oldState, newState) {
      return { ...oldState, ...newState };
    }

    switch (action.type) {
      case ACTIONS.PROCESS_URL: {
        const cookies = new Cookies();

        resolveUtmCookieUpdates(
          INITIAL_SEARCH_PARAMS,
          INITIAL_PATHNAME,
        ).forEach(({ key, value }) => cookies.set(key, value, { path: '/' }));

        const { googleId, googleIdType } = getGoogleClickIdentifier(
          INITIAL_SEARCH_PARAMS.entries(),
        );
        if (googleId) {
          GOOGLE_CLICK_ID_PARAMS.forEach(param => {
            if (param !== googleIdType) cookies.remove(param, { path: '/' });
          });
          cookies.set(googleIdType, googleId, { path: '/' });
        }

        return { ...oldState, ...newState };
      }

      case ACTIONS.UPDATE_STATE:
        return getNewState(oldState, action.newState);
      default:
        break;
    }
  }, initialState);

  useEffect(() => {
    dispatch({ type: ACTIONS.PROCESS_URL });
  }, []);

  const loadBanners = async () => {
    await makeHTTPProvider()
      .get('/banners')
      .then(banners => {
        dispatch({
          type: ACTIONS.UPDATE_STATE,
          newState: { banners },
        });
      })
      .catch(error => {
        if (process.env.NODE_ENV !== 'test') {
          logger.debug(error);
        }
      });
  };

  useEffect(() => {
    if (i18n.language !== 'pt') return;

    loadBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <AppContext.Provider
      value={{
        globalState: state,
        isUpMd,
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
}

export { AppContext, AuthProvider };
